import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { Phone, Battery, MapPin, Shield, Settings, Smartphone } from "lucide-react";

const FeaturesSection = () => {
  const { t, language } = useLanguage();

  const features = [
    { icon: Phone, title: t("feature_calls_title"), desc: t("feature_calls_desc"), color: "from-green-500 to-emerald-500", emoji: "📞" },
    { icon: Battery, title: t("feature_battery_title"), desc: t("feature_battery_desc"), color: "from-yellow-500 to-orange-500", emoji: "🔋" },
    { icon: MapPin, title: t("feature_gps_title"), desc: t("feature_gps_desc"), color: "from-blue-500 to-indigo-500", emoji: "📍" },
    { icon: Shield, title: t("feature_security_title"), desc: t("feature_security_desc"), color: "from-purple-500 to-pink-500", emoji: "🛡️" },
    { icon: Settings, title: t("feature_control_title"), desc: t("feature_control_desc"), color: "from-pink-500 to-rose-500", emoji: "⚙️" },
    { icon: Smartphone, title: language === 'fr' ? "Tous opérateurs" : "All carriers", desc: language === 'fr' ? "Compatible Orange, SFR, Bouygues, Free... Utilisez le forfait de votre choix dès 2€/mois." : "Compatible with Orange, SFR, Bouygues, Free... Use any plan from €2/month.", color: "from-cyan-500 to-blue-500", emoji: "📶" },
  ];

  return (
    <section id="features" className="calli-section gradient-warm pattern-dots relative overflow-hidden" data-testid="features-section">
      {/* Background */}
      <div className="glow-orb w-[600px] h-[600px] bg-purple-200 -top-40 -left-40"></div>
      <div className="glow-orb w-[400px] h-[400px] bg-orange-200 bottom-0 right-0"></div>
      
      <div className="calli-container relative z-10">
        <div className="text-center mb-16 space-y-4">
          <span className="calli-badge">✨ {t("features_badge")}</span>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900">
            {t("features_title")}
          </h2>
          <p className="text-xl text-gray-600">{t("features_subtitle")}</p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`calli-card rounded-3xl p-6 group cursor-pointer ${index === 0 ? 'sm:col-span-2 lg:col-span-1' : ''}`}
              data-testid={`feature-card-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <span className="text-2xl">{feature.emoji}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold mb-2 text-gray-900 group-hover:text-orange-500 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Row */}
        <div className="mt-16 grid grid-cols-3 gap-4">
          {[
            { value: "0", label: language === 'fr' ? 'Écran' : 'Screen', color: 'from-purple-500 to-pink-500' },
            { value: "8", label: language === 'fr' ? 'Contacts' : 'Contacts', color: 'from-orange-500 to-yellow-500' },
            { value: "15", label: language === 'fr' ? 'Jours' : 'Days', color: 'from-green-500 to-emerald-500' },
          ].map((stat, i) => (
            <div key={i} className="calli-card rounded-2xl p-6 text-center">
              <div className={`text-5xl sm:text-6xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                {stat.value}
              </div>
              <p className="text-gray-600 text-sm mt-2 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
